import React from 'react';

type RecordMeltActionsProps = {
  primary: React.ReactNode;
  secondary: React.ReactNode;
  /** Accessible name of the narrow-width "More" menu, e.g. "More event actions". */
  moreLabel?: string;
};

/**
 * A melt bar's actions: the primary ones always show; the secondary ones show inline on wide
 * screens and collapse into a "More" menu below 1320px (the switch lives in global.css under the
 * `event-detail-melt-actions__*` classes, kept from Events, where this started).
 */
export default function RecordMeltActions({
  primary,
  secondary,
  moreLabel = 'More actions',
}: RecordMeltActionsProps) {
  const [open, setOpen] = React.useState(false);
  const overflowRef = React.useRef<HTMLDivElement | null>(null);
  const menuId = React.useId();

  React.useEffect(() => {
    if (!open) return undefined;

    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target;
      if (target instanceof Node && overflowRef.current?.contains(target)) return;
      setOpen(false);
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };

    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open]);

  const handleSecondaryAction = React.useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    const target = event.target as HTMLElement | null;
    const interactive = target?.closest(
      'a, button, [role="menuitem"], [data-rh-menu-dismiss], [data-rh-menu-keep-open]',
    );
    if (!(interactive instanceof HTMLElement)) return;
    if (interactive.hasAttribute('data-rh-menu-keep-open')) return;
    if (interactive.getAttribute('data-rh-menu-dismiss') === 'false') return;
    setOpen(false);
  }, []);

  return (
    <>
      {primary}
      {secondary ? (
        <>
          <div className="event-detail-melt-actions__secondary-inline">
            {secondary}
          </div>
          <div ref={overflowRef} className="event-detail-melt-actions__overflow">
            <button
              type="button"
              className="btn subtle event-detail-melt-actions__trigger"
              aria-expanded={open}
              aria-controls={menuId}
              onClick={() => setOpen(value => !value)}
            >
              More
            </button>
            {open ? (
              <div
                id={menuId}
                className="event-detail-melt-actions__menu"
                role="group"
                aria-label={moreLabel}
                onClick={handleSecondaryAction}
              >
                {secondary}
              </div>
            ) : null}
          </div>
        </>
      ) : null}
    </>
  );
}
